import {
  type CreditLimits,
  CreditLimitsSchema,
  type ResearchLimits,
  ResearchLimitsSchema,
} from '@littlebird/sdk';
import { z } from 'zod';

import {
  type RewardType,
  RewardTypeSchema,
} from '@/features/referral/referral.types';

export enum SubscriptionPlan {
  BASIC = 'basic',
  PLUS_MONTHLY = 'plus_monthly',
  PLUS_ANNUAL = 'plus_annual',
  POWER_MONTHLY = 'power_monthly',
  POWER_ANNUAL = 'power_annual',
  PRO_MONTHLY = 'pro_monthly',
  PRO2_MONTHLY = 'pro2_monthly',
}

export const SubscriptionPlanEnum = z.enum([
  SubscriptionPlan.BASIC,
  SubscriptionPlan.PLUS_MONTHLY,
  SubscriptionPlan.PLUS_ANNUAL,
  SubscriptionPlan.POWER_MONTHLY,
  SubscriptionPlan.POWER_ANNUAL,
  SubscriptionPlan.PRO_MONTHLY,
  SubscriptionPlan.PRO2_MONTHLY,
]);

export const CheckoutPlanEnum = SubscriptionPlanEnum.exclude([
  SubscriptionPlan.BASIC,
]);

export const CreateCheckoutSessionResponseSchema = z.object({
  session_id: z.string(),
  url: z.string(),
});

export const CreatePortalSessionResponseSchema = z.object({
  url: z.string(),
});

export const SubscriptionStatusResponseSchema = z.object({
  current_plan: SubscriptionPlanEnum,
  mcp_server_permitted: z.boolean(),
  current_plan_display_name: z.string().nullable().optional(),
  plan_change_date: z.string().nullable(),
  stripe_customer_id: z.string().nullable(),
  cancel_at_period_end: z.boolean(),
  subscription_status: z.string().optional(),
  current_period_end: z.number().nullable().optional(),
  subscription_id: z.string().nullable().optional(),
  trial_days_remaining: z.number().nullable().optional(),
  plus_promo_offer: z
    .object({
      expires_at: z.string(),
      trial_days: z.number(),
    })
    .nullable()
    .optional(),
  power_upgrade_offer: z
    .object({
      expires_at: z.string(),
    })
    .nullable()
    .optional(),
  payment_method: z
    .object({
      brand: z.string(),
      last4: z.string(),
    })
    .nullable()
    .optional(),
  ended_in_trial: z.boolean().optional(),
  meeting_limits: z
    .object({
      meetings_used: z.number(),
      meetings_limit: z.number(),
      meetings_remaining: z.number(),
      is_limit_hit: z.boolean(),
      meetings_limit_type: z.string(),
      warning_threshold: z.number(),
    })
    .optional(),
  journal_limits: z
    .object({
      unavailable_before: z.string().nullable(),
    })
    .optional(),
  routines_limits: z
    .object({
      routines_created: z.number(),
      routines_limit: z.number(),
      max_enabled: z.boolean().optional(),
      ultra_enabled: z.boolean().optional(),
      god_enabled: z.boolean().optional(),
      argon_enabled: z.boolean().optional(),
    })
    .optional(),
  research_limits: ResearchLimitsSchema.optional(),
  has_applied_referral: z.boolean().optional(),
  referral_status: z.enum(['pending', 'successful']).nullable().optional(),
  referral_reward_type: RewardTypeSchema.nullable().optional(),
  user_referral_reward_type: RewardTypeSchema.nullable().optional(),

  is_free: z.boolean().optional(),
  account_credit_cents: z.number().optional(),
  credit_balance: z
    .object({
      plan_balance: z.number(),
      purchased_balance: z.number(),
      total: z.number(),
      team_pool_available: z.number().optional(),
      low_balance_threshold: z.number().optional(),
      lite_mode_active: z.boolean().optional(),
    })
    .optional(),
  credit_limits: CreditLimitsSchema.optional(),
  auto_refill: z
    .object({
      enabled: z.boolean(),
      threshold_credits: z.number().nullable().optional(),
      threshold_value_cents: z.number().nullable().optional(),
      topup_pack_id: z.string().nullable().optional(),
      topup_credits: z.number().nullable().optional(),
      topup_cents: z.number().nullable().optional(),
      topup_value_cents: z.number().nullable().optional(),
      monthly_cap_cents: z.number().nullable().optional(),
      last_failure_reason: z.string().nullable().optional(),
    })
    .optional(),

  team: z
    .object({
      id: z.string(),
      name: z.string(),
      role: z.string(),
      tier: z.string(),
      allowed_chat_modes: z.array(z.string()).nullable().optional(),
    })
    .nullable()
    .optional(),
  is_team_member: z.boolean().optional(),
});

export type CheckoutReturnChannel = 'imessage' | 'whatsapp';

/** The app that starts a Stripe session; the backend picks the return URL from it */
export type CheckoutClient = 'web' | 'desktop';

export type CreateCheckoutSessionRequest = {
  price_id: string;
  return_channel?: CheckoutReturnChannel;
  return_to?: 'channels' | 'home';
  client?: CheckoutClient;
};

export type CreatePortalSessionRequest = {
  return_channel?: CheckoutReturnChannel;
  client?: CheckoutClient;
  add_card?: boolean;
};

export type CreateCheckoutSessionResponse = z.infer<
  typeof CreateCheckoutSessionResponseSchema
>;
export type CreatePortalSessionResponse = z.infer<
  typeof CreatePortalSessionResponseSchema
>;
export type SubscriptionStatusResponse = z.infer<
  typeof SubscriptionStatusResponseSchema
>;

export interface PricingPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  original_price?: number | null;
  currency: string;
  interval: string;
  stripe_price_id: string;
  features: string[];
  popular: boolean;
}

export interface PricingPlansResponse {
  plans: PricingPlan[];
}

export const PricingPlansResponseSchema = z.object({
  plans: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
      price: z.number(),
      original_price: z.number().nullable().optional(),
      currency: z.string(),
      interval: z.string(),
      stripe_price_id: z.string(),
      features: z.array(z.string()),
      popular: z.boolean(),
    })
  ),
});

export interface MeetingLimits {
  meetings_used: number;
  meetings_limit: number;
  meetings_remaining: number;
  is_limit_hit: boolean;
  meetings_limit_type: string;
  warning_threshold: number;
}

export interface SubscriptionState {
  currentPlan: SubscriptionPlan;
  mcpServerPermitted: boolean;
  currentPlanDisplayName: string | null;
  planChangeDate: string | null;
  stripeCustomerId: string | null;
  cancelAtPeriodEnd: boolean;
  subscriptionStatus: string | undefined;
  currentPeriodEnd: number | undefined;
  subscriptionId: string | null | undefined;
  trialDaysRemaining: number | null | undefined;
  plusPromoOffer: { expiresAt: string; trialDays: number } | null;
  powerUpgradeOffer: { expiresAt: string } | null;
  paymentMethod:
    | {
        brand: string;
        last4: string;
      }
    | null
    | undefined;
  endedInTrial: boolean;
  meeting_limits: MeetingLimits | undefined;
  researchLimits: ResearchLimits | undefined;
  journalLimits:
    | {
        unavailable_before: string | null;
      }
    | undefined;
  routinesLimits:
    | {
        routines_created: number;
        routines_limit: number;
        max_enabled?: boolean;
        ultra_enabled?: boolean;
        god_enabled?: boolean;
        argon_enabled?: boolean;
      }
    | undefined;
  hasAppliedReferral: boolean | undefined;
  isFree: boolean;
  referralStatus: 'pending' | 'successful' | null | undefined;
  referralRewardType: RewardType | null;
  userReferralRewardType: RewardType | null;
  accountCreditCents: number | undefined;
  creditBalance: number;
  creditTotal: number;
  teamPoolAvailable: number;
  lowBalanceThreshold: number;
  liteModeActive: boolean;
  creditLimits: CreditLimits | undefined;
  autoRefill: AutoRefillSettings;
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
  pricingPlans: PricingPlan[];
  pricingPlansLoading: boolean;
  pricingPlansError: string | null;
  team: {
    id: string;
    name: string;
    role: string;
    tier: string;
    allowed_chat_modes?: string[] | null;
  } | null;
  isTeamMember: boolean;
}

export const planDisplayTextMap: Record<SubscriptionPlan, string> = {
  [SubscriptionPlan.BASIC]: 'Basic',
  [SubscriptionPlan.PLUS_MONTHLY]: 'Plus',
  [SubscriptionPlan.PLUS_ANNUAL]: 'Plus',
  [SubscriptionPlan.POWER_MONTHLY]: 'Power',
  [SubscriptionPlan.POWER_ANNUAL]: 'Power',
  [SubscriptionPlan.PRO_MONTHLY]: 'Pro',
  [SubscriptionPlan.PRO2_MONTHLY]: 'Pro',
};

export function formatPriceValue(price: number): string {
  return Number.isInteger(price) ? price.toString() : price.toFixed(2);
}

export function formatBillingDate(
  timestamp: number | null | undefined
): string | null {
  if (!timestamp) return null;
  return new Date(timestamp * 1000).toLocaleDateString();
}

// Cancellation flow types
export const RetentionOfferSchema = z.object({
  display_amount: z.string(),
  duration_label: z.string(),
});
export type RetentionOffer = z.infer<typeof RetentionOfferSchema>;

export const CancellationContextResponseSchema = z.object({
  is_eligible_for_retention: z.boolean(),
  retention_offer: RetentionOfferSchema.nullish(),
});
export type CancellationContextResponse = z.infer<
  typeof CancellationContextResponseSchema
>;

export const ApplyRetentionOfferResponseSchema = z.object({
  success: z.boolean(),
});
export type ApplyRetentionOfferResponse = z.infer<
  typeof ApplyRetentionOfferResponseSchema
>;

export const ApplyPromoCodeResponseSchema = z.object({
  success: z.boolean(),
  discount_description: z.string(),
});
export type ApplyPromoCodeResponse = z.infer<
  typeof ApplyPromoCodeResponseSchema
>;

export type UpgradeToProRequest = {
  price_id: string;
};

export const UpgradeToProResponseSchema = z.object({
  success: z.boolean(),
});
export type UpgradeToProResponse = z.infer<typeof UpgradeToProResponseSchema>;

export type UpgradeToPowerRequest = {
  billing_interval: 'monthly' | 'annual';
};

export const UpgradeToPowerResponseSchema = z.object({
  success: z.boolean(),
});
export type UpgradeToPowerResponse = z.infer<
  typeof UpgradeToPowerResponseSchema
>;

export function getDiscountedPrice(
  price: number,
  discountPercent: number
): number {
  return Math.round(price * (1 - discountPercent / 100) * 100) / 100;
}

export const InvoiceItemSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  date: z.number(),
  amount_paid: z.number(),
  currency: z.string(),
  status: z.string(),
  invoice_pdf: z.string().nullable(),
  hosted_invoice_url: z.string().nullable(),
});

export const InvoiceListResponseSchema = z.object({
  invoices: z.array(InvoiceItemSchema),
});
export type InvoiceListResponse = z.infer<typeof InvoiceListResponseSchema>;

const RoutineSpendItemSchema = z.object({
  routine_id: z.string().nullable(),
  name: z.string(),
  credits: z.number(),
  count: z.number(),
});

export const CreditBalanceResponseSchema = z.object({
  plan_balance: z.number(),
  daily_balance: z.number(),
  purchased_balance: z.number(),
  rate_cents_per_1000: z.number().optional(),
  plan_grant_amount: z.number(),
  plan_grant_kind: z.enum(['monthly', 'one_time']).nullish(),
  daily_grant_amount: z.number(),
  overage_credits: z.number().optional(),
  spend_by_feature: z.array(
    z.object({
      feature_key: z.string(),
      credits: z.number(),
      count: z.number(),
    })
  ),
  spend_by_routine: z.array(RoutineSpendItemSchema),
  spend_by_routine_cycle: z.array(RoutineSpendItemSchema).optional(),
  spend_by_mode: z
    .array(
      z.object({
        mode: z.string(),
        credits: z.number(),
        count: z.number(),
      })
    )
    .optional(),
  cycle_resets_at: z.string().optional(),
});
export type CreditBalanceResponse = z.infer<typeof CreditBalanceResponseSchema>;

export const CreditPackItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  credits: z.number(),
  price_cents: z.number(),
  apple_product_id: z.string().nullable().optional(),
});

export const UsageBundleItemSchema = z.object({
  id: z.string(),
  value_cents: z.number(),
  price_cents: z.number(),
  discount_pct: z.number(),
  credits: z.number(),
  apple_product_id: z.string().nullable().optional(),
});

export const DiscountBracketSchema = z.object({
  min_cents: z.number(),
  discount_pct: z.number(),
});

export const CreditPacksResponseSchema = z.object({
  packs: z.array(CreditPackItemSchema),
  bundles: z.array(UsageBundleItemSchema).optional(),
  discount_brackets: z.array(DiscountBracketSchema).optional(),
  rate_cents_per_1000: z.number().optional(),
  min_credits: z.number().optional(),
  max_credits: z.number().optional(),
  min_value_cents: z.number().optional(),
  max_value_cents: z.number().optional(),
});
export type CreditPacksResponse = z.infer<typeof CreditPacksResponseSchema>;

export type PurchaseCreditPackRequest = {
  value_cents?: number;
  credits?: number;
  pack_id?: string;
  idempotency_key: string;
};

export const PurchaseCreditPackResponseSchema = z.object({
  status: z.enum(['succeeded', 'requires_action', 'failed']),
  message: z.string().nullable().optional(),
  new_balance: z.number().nullable().optional(),
  checkout_url: z.string().nullable().optional(),
});
export type PurchaseCreditPackResponse = z.infer<
  typeof PurchaseCreditPackResponseSchema
>;

export const AutoRefillSettingsSchema = z.object({
  enabled: z.boolean(),
  threshold_credits: z.number().nullable().optional(),
  threshold_value_cents: z.number().nullable().optional(),
  topup_pack_id: z.string().nullable().optional(),
  topup_credits: z.number().nullable().optional(),
  topup_cents: z.number().nullable().optional(),
  topup_value_cents: z.number().nullable().optional(),
  monthly_cap_cents: z.number().nullable().optional(),
  last_failure_reason: z.string().nullable().optional(),
});
export type AutoRefillSettings = z.infer<typeof AutoRefillSettingsSchema>;

export type UpdateAutoRefillRequest = {
  enabled: boolean;
  threshold_value_cents?: number | null;
  topup_value_cents?: number | null;
  monthly_cap_cents?: number | null;
};
